/**
 * Azure AD Role Mapping Configuration
 * Maps Azure AD role GUIDs to friendly names used in the application
 */

// Azure AD Application Roles (these GUIDs come from your Azure AD app registration)
export const AZURE_AD_ROLES = {
  // Standard Azure AD Directory Roles
  GLOBAL_ADMINISTRATOR: '62e90394-69f5-4237-9190-012177145e10',
  USER_ADMINISTRATOR: 'fe930be7-5e62-47db-91af-98c3a49a38b1',
  APPLICATION_ADMINISTRATOR: '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3',

  // RBC Fleet Management Azure AD Groups
  RBC_FLEET_ADMINISTRATORS: 'e8ada2ec-2575-4b5a-b8ed-bd49f4726edc',
  RBC_FLEET_SCHEDULERS: '754f1831-5c78-42ba-8e96-8d080c349e4b',
  RBC_FLEET_MAINTENANCE: '686458b0-98b4-4efb-b702-85997262755e',
  RBC_FLEET_VIEWERS: 'fb7b3723-991a-46ba-9e80-f24d5f5e0db3',
  RBC_FLEET_SAFETY: 'c47b06d3-c8a5-4497-aaca-212841f443ea',
  RBC_FLEET_TVDISPLAY: '55aded18-2b6d-4c31-af19-1ac768fa4128',

  // Other Azure AD Groups that James is member of (unknown purpose - may be unrelated to this app)
  UNKNOWN_GROUP_1: 'd3277c20-b60b-46a9-bc74-4f8f292bd7e1',
  UNKNOWN_GROUP_2: 'c93b1836-feae-4017-a159-9af632e90ea5',
  UNKNOWN_GROUP_3: 'f8f7ee3c-224a-46b5-9151-b69d65c9de5e',
  UNKNOWN_GROUP_4: 'ac98bd41-14de-4a2a-afaf-45093d2d28f2',
  UNKNOWN_GROUP_5: 'ee4ef67f-2d5c-4460-89b5-2ce7c0829825',
  UNKNOWN_GROUP_6: '749f809f-c368-44c0-862b-8bab473f0178',
  UNKNOWN_GROUP_7: '468597a3-e0f3-4922-9ee6-b5bfd95e30d6',
  UNKNOWN_GROUP_8: '7a8a84a9-31db-4082-a299-0fcc6976b85a',
  UNKNOWN_GROUP_9: '22c845c6-a0ec-4cee-931d-7101491c2bdb',
  UNKNOWN_GROUP_10: '033d96dd-5a93-42f0-a6cb-158e1f5ed613',
  UNKNOWN_GROUP_11: '988b831d-1d06-4b88-b9b1-619ca2849ea7',
  UNKNOWN_GROUP_12: '9210c778-76e0-4412-a5bb-7593bd5050c1',
  UNKNOWN_GROUP_13: '0c812898-217e-445e-bc5d-492e3a761e96',
  UNKNOWN_GROUP_14: 'd240eca6-45b6-44d9-a7ef-074cefc47567',
  UNKNOWN_GROUP_15: '36c691ba-b435-4f8a-8cbb-45e162657553',
  UNKNOWN_GROUP_16: '740b03de-aabf-4cff-9386-41fa03ec31a1'
};

/**
 * Role mapping from Azure AD GUIDs to application role names
 * Maps Azure AD Group IDs to application roles
 */
export const ROLE_MAPPING = {
  // Map standard Azure AD directory roles to admin (unlikely to be present but included for completeness)
  [AZURE_AD_ROLES.GLOBAL_ADMINISTRATOR]: 'admin',
  [AZURE_AD_ROLES.USER_ADMINISTRATOR]: 'admin',
  [AZURE_AD_ROLES.APPLICATION_ADMINISTRATOR]: 'admin',

  // Map RBC Fleet Management Groups to application roles
  [AZURE_AD_ROLES.RBC_FLEET_ADMINISTRATORS]: 'admin',
  [AZURE_AD_ROLES.RBC_FLEET_SCHEDULERS]: 'scheduler',
  [AZURE_AD_ROLES.RBC_FLEET_MAINTENANCE]: 'maintenance',
  [AZURE_AD_ROLES.RBC_FLEET_VIEWERS]: 'viewer',
  [AZURE_AD_ROLES.RBC_FLEET_SAFETY]: 'safety',
  [AZURE_AD_ROLES.RBC_FLEET_TVDISPLAY]: 'tvdisplay'

  // Note: Unknown groups are not mapped - they appear to be other Azure AD groups
  // that James is a member of but are not related to the Fleet Management application
};

/**
 * Application role names used throughout the app
 * These roles control access to different features:
 *
 * - admin: Full access to all features, can manage users, schedules, and system settings
 * - scheduler: Can create, edit, and delete bus schedules and driver assignments
 * - maintenance: Can view and update maintenance records and schedules
 * - safety: Can view safety events, driver performance, and configure safety alerts
 * - viewer: Read-only access to schedules and fleet status
 * - tvdisplay: TV Display kiosk mode - auto-redirects to fullscreen TV dashboard on login
 * - authenticated: Basic authenticated user (automatically assigned to all logged-in users)
 */
export const APP_ROLES = {
  ADMIN: 'admin',
  SCHEDULER: 'scheduler',
  VIEWER: 'viewer',
  MAINTENANCE: 'maintenance',
  SAFETY: 'safety',
  TVDISPLAY: 'tvdisplay',
  AUTHENTICATED: 'authenticated'
};

/**
 * Map Azure AD role GUIDs to friendly names
 * @param {string[]} azureRoles - Array of Azure AD role GUIDs
 * @returns {string[]} Array of friendly role names
 */
export const mapAzureRolesToAppRoles = azureRoles => {
  if (!azureRoles || !Array.isArray(azureRoles)) {
    return [APP_ROLES.AUTHENTICATED]; // Default to authenticated if no roles
  }

  const mappedRoles = new Set([APP_ROLES.AUTHENTICATED]); // Always include authenticated

  azureRoles.forEach(roleGuid => {
    const mappedRole = ROLE_MAPPING[roleGuid];
    if (mappedRole) {
      mappedRoles.add(mappedRole);
    }
  });

  return Array.from(mappedRoles);
};

/**
 * Check if user has a specific role
 * @param {string[]} userRoles - User's Azure AD role GUIDs
 * @param {string} requiredRole - Required application role name
 * @returns {boolean}
 */
export const userHasRole = (userRoles, requiredRole) => {
  const mappedRoles = mapAzureRolesToAppRoles(userRoles);
  return mappedRoles.includes(requiredRole);
};

/**
 * Check if user has any of the specified roles
 * @param {string[]} userRoles - User's Azure AD role GUIDs
 * @param {string[]} requiredRoles - Array of required application role names
 * @returns {boolean}
 */
export const userHasAnyRole = (userRoles, requiredRoles) => {
  const mappedRoles = mapAzureRolesToAppRoles(userRoles);
  return requiredRoles.some(role => mappedRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 * @param {string[]} userRoles - User's Azure AD role GUIDs
 * @param {string[]} requiredRoles - Array of required application role names
 * @returns {boolean}
 */
export const userHasAllRoles = (userRoles, requiredRoles) => {
  const mappedRoles = mapAzureRolesToAppRoles(userRoles);
  return requiredRoles.every(role => mappedRoles.includes(role));
};
